import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Card,
  CardContent,
  Box,
  Switch,
  Button,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Notifications,
  Warning,
  AccountBalanceWallet,
  Storefront,
  Email,
  ArrowBack
} from '@mui/icons-material';
import merchantAPI from '../../../services/merchantAPI';
import { useNavigate } from 'react-router-dom';
import FlutterServicesAPI from '../../../services/flutterServices';
import { AutoCloseAlert } from '../../Common';
import { useMerchantPermissions } from '../../../contexts/MerchantPermissionContext';
import useRouteMeta from '../../../hooks/useRouteMeta';

const NotificationsPage = () => {
  const navigate = useNavigate();
  useRouteMeta();
  const [notifications, setNotifications] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { hasPermission } = useMerchantPermissions();

  useEffect(() => {
    loadNotificationSettings();
  }, []);

  const loadNotificationSettings = async () => {
    setLoading(true);
    try {
      const response = await merchantAPI.getNotificationSettings();
      setNotifications(response.data.settings);
    } catch (error) {
      console.error('Failed to load notification settings:', error);
      setError('Failed to load notification settings');
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationUpdate = async (settingType, value) => {
    try {
      const updatedSettings = {
        ...notifications,
        [settingType]: value
      };
      await merchantAPI.updateNotificationSettings(updatedSettings);
      setNotifications(updatedSettings);
      setSuccess('Notification settings updated!');
    } catch (error) {
      console.error('Failed to update notification settings:', error);
      setError('Failed to update notification settings');
    }
  };

  const notificationTypes = [
    {
      key: 'generation_complete',
      icon: <Notifications />,
      title: 'Generation Complete',
      description: 'Notify when AI generation is completed'
    },
    {
      key: 'low_credits_alert',
      icon: <Warning />,
      title: `Low ${FlutterServicesAPI.getCreditLabel()} Alert`,
      description: `Notify when ${FlutterServicesAPI.getCreditLabel().toLowerCase()} balance is low`
    },
    {
      key: 'refund_notifications',
      icon: <AccountBalanceWallet />,
      title: 'Refund Notifications',
      description: 'Notify when refunds are processed'
    },
    {
      key: 'credit_request_notifications',
      icon: <Storefront />,
      title: `${FlutterServicesAPI.getCreditLabel()} Request Notifications`,
      description: `Notify when ${FlutterServicesAPI.getCreditLabel().toLowerCase()} requests are approved or rejected`
    }
  ];

  const switchStyles = {
    '& .MuiSwitch-switchBase.Mui-checked': {
      color: '#6318C6',
      '&:hover': {
        backgroundColor: 'rgba(99, 24, 198, 0.08)'
      }
    },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
      backgroundColor: '#6318C6'
    }
  };

  return (
    <Container className="mt-mb-0" maxWidth="lg" sx={{ mt: { xs: 0, sm: 0, md: 4 }, mb: { xs: 0, sm: 2, md: 4 }, px: { xs: 0, sm: 2, md: 3 } }}>
      <Paper elevation={0} sx={{ p: { xs: 0.5, sm: 2, md: 4 }, pt: { xs: 0, sm: 3, md: 3 }, boxShadow: 'none', border: { xs: 'none', sm: '1px solid #e5e7eb', md: '1px solid #e5e7eb' }, bgcolor: { xs: 'transparent', sm: 'white', md: 'white' }, borderRadius: '8px' }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} px={{ xs: 0.5, sm: 0 }}>
          <Button onClick={() => navigate('/dashboard')}
            startIcon={
              <svg xmlns="http://www.w3.org/2000/svg" width="8" height="13" viewBox="0 0 8 13" fill="none">
                <path d="M6.41309 0.916079L0.916291 6.41287L6.41309 11.9097" stroke="#651BC6" strokeWidth="1.83226" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            }
            sx={{ color: '#4A5565', px: 0, textTransform: 'none', fontFamily: 'var(--font-montserrat-medium)', fontWeight: 500, fontSize: '17.59px', '&:hover': { background: 'transparent', opacity: 0.8 } }}>
            Back
          </Button>
          <Typography variant="h6" sx={{ color: '#1E2939', fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, fontSize: '17.59px' }}>Notifications</Typography>
        </Box>

        <Box display="flex" justifyContent="flex-start" alignItems="center" mb={1} mt={0.1} px={{ xs: 1, sm: 0 }}>
          <Typography sx={{ fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, fontSize: '16px', color: '#101828' }}>
            Notification Preferences
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
        <AutoCloseAlert severity="success" message={success} show={!!success} onClose={() => setSuccess('')} autoCloseDelay={4000} />

        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress sx={{ color: '#6318C6' }} />
          </Box>
        ) : (
          <Box mt={2} px={{ xs: 0, sm: 0 }}>
            {notificationTypes.map((notif) => (
              <Card key={notif.key} elevation={0} sx={{
                borderRadius: '16px',
                boxShadow: '0px 2px 12.01px 0px rgba(0, 0, 0, 0.08)',
                mb: 3,
                border: 'none',
                bgcolor: 'white'
              }}>
                <CardContent sx={{ p: '24px !important' }}>
                  <Typography sx={{ color: '#101828', fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, fontSize: '16px', mb: 0.5 }}>
                    {notif.title}
                  </Typography>
                  <Typography sx={{ color: '#6A7282', fontFamily: 'var(--font-montserrat-regular)', fontWeight: 400, fontSize: '14px', mb: 3 }}>
                    {notif.description}
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Email Setting */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Box sx={{ width: 32, height: 32, backgroundColor: '#F3F4F6', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <img src="/assets/img/website/icons/email-not.svg" alt="Email" style={{ width: '16px', height: '16px' }} />
                        </Box>
                        <Typography sx={{ color: '#364153', fontFamily: 'var(--font-montserrat-medium)', fontWeight: 500, fontSize: '14px' }}>
                          Email Notification
                        </Typography>
                      </Box>
                      <Switch checked={notifications[`${notif.key}_email`] || false} onChange={(e) => handleNotificationUpdate(`${notif.key}_email`, e.target.checked)} sx={switchStyles} />
                    </Box>

                    {/* Push Setting */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Box sx={{ width: 32, height: 32, backgroundColor: '#F3F4F6', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <img src="/assets/img/website/icons/push-not.svg" alt="Push" style={{ width: '16px', height: '16px' }} />
                        </Box>
                        <Typography sx={{ color: '#364153', fontFamily: 'var(--font-montserrat-medium)', fontWeight: 500, fontSize: '14px' }}>
                          Push Notification
                        </Typography>
                      </Box>
                      <Switch checked={notifications[`${notif.key}_push`] || false} onChange={(e) => handleNotificationUpdate(`${notif.key}_push`, e.target.checked)} sx={switchStyles} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default NotificationsPage;
